import { SlotDesign, CardLayer, CardShapeLayer, CardTextLayer, CardImageLayer, CardSvgLayer, CardSvgPath, CardGradient } from '../design-data';

// ─────────────────────────────────────────────────────────────────────────────
// Localized content support for template text layers.
//
// Template text layers may carry either a plain string (already resolved) or a
// locale map `{ sl: '...', en: '...', ... }`.  The resolver collapses the map
// to a plain string for the active locale before the slots are applied to the
// canvas or rendered as thumbnails.
// ─────────────────────────────────────────────────────────────────────────────

export type LocalizedContent = string | Record<string, string>;

/**
 * Pick the best string from a LocalizedContent value for the given locale.
 * Falls back: exact locale → language subtag (e.g. 'en' from 'en-GB') → 'en'
 * → 'sl' (treat 'si'↔'sl' as aliases) → first value.
 */
export function pickLocale(c: LocalizedContent, locale: string): string {
  if (typeof c === 'string') return c;
  // Normalise 'si' → 'sl' as an alias.
  const loc = locale === 'si' ? 'sl' : locale;
  if (loc in c) return c[loc];
  // language subtag (e.g. 'en' from 'en-GB')
  const lang = loc.split('-')[0];
  if (lang && lang in c) return c[lang];
  if ('en' in c) return c['en'];
  if ('sl' in c) return c['sl'];
  const first = Object.values(c)[0];
  return first ?? '';
}

/**
 * Normalize the legacy " Bold" font-family convention on a single text layer.
 *
 * Historically the bold toggle appended the literal string " Bold" to the font
 * FAMILY (e.g. "Montserrat" → "Montserrat Bold") instead of setting a real font
 * weight. "Montserrat Bold" was never a registered family, so browsers silently
 * fell back to a default font — the bold toggle appeared to "change the font".
 *
 * This strips a trailing " Bold" off `font` and sets `bold: true` instead, so
 * boldness is expressed as a real weight (see CardTextLayer.bold) applied by
 * both renderers. Idempotent: already-normalized layers (no " Bold" suffix)
 * pass through unchanged. Non-text layers pass through unchanged.
 *
 * Call this at every choke point where a text layer can enter the editor from
 * outside the current session: templates loaded from the DB, restored
 * autosave drafts, and pasted designs. `resolveTemplateSlots` — the shared
 * funnel used by all of those paths — applies it below.
 */
export function normalizeTextLayerFont<L extends { type: string; font?: string; bold?: boolean }>(
  layer: L,
): L {
  if (layer.type !== 'text' || typeof layer.font !== 'string') return layer;
  if (!layer.font.endsWith(' Bold')) return layer;
  return {
    ...layer,
    font: layer.font.slice(0, -' Bold'.length),
    bold: true,
  };
}

/**
 * Deep-clone a list of SlotDesign objects and resolve every text layer's
 * `content` from a LocalizedContent map to a plain string for the given locale.
 * Also normalizes any legacy " Bold"-suffixed font family (see
 * `normalizeTextLayerFont`). Call this before applying template slots to the
 * canvas or rendering thumbnails.
 */
export function resolveTemplateSlots(slots: SlotDesign[], locale: string): SlotDesign[] {
  return (slots ?? []).map((slot) => ({
    ...slot,
    // Tolerate malformed slots (e.g. a corrupt template row with no `layers`)
    // rather than crashing the whole editor.
    layers: (slot?.layers ?? []).map((layer) => {
      if (layer.type !== 'text') return layer;
      const content = layer.content as LocalizedContent;
      return normalizeTextLayerFont({ ...layer, content: pickLocale(content, locale) });
    }),
  }));
}

/**
 * A starter template the customer picks from the gallery. The design quality of
 * these is the selling point — each one is a coherent, hand-tuned composition
 * (palette + font pairing + shape bands/frames + a single photo placeholder).
 *
 * `name` is a locale map (keyed by BCP-47 locale, e.g. `sl`, `en`, `hr`, …).
 * Render with `name[locale] ?? name.en ?? name.sl`. `categories` are
 * occasion/style slugs used by the picker filter. `thumbnailUrl`, if present,
 * points at a pre-rendered preview PNG in /public; otherwise the LeftRail
 * falls back to a styled box built from the front slot.
 */
export interface CardTemplate {
  id: string;
  kind: string;
  /** Occasion/style slugs (see constants.ts TEMPLATE_CATEGORIES). */
  categories: string[];
  name: { [locale: string]: string };
  thumbnailUrl?: string;
  slots: SlotDesign[];
  /**
   * Recommended paper ids for this template — a RECOMMENDATION, never a hard
   * limit. All of the product's offered papers stay selectable; these ids
   * (intersected with what the product actually offers) are badged and sorted
   * first in the paper picker. Empty = no recommendation.
   */
  paperIds: string[];
  /**
   * The paper to preselect when this template is applied (if the product
   * offers it) and to use as the backdrop color behind the template's
   * rendered thumbnail. `null` = no showcase paper set.
   */
  showcasePaperId: string | null;
}

export type { SlotDesign, CardLayer, CardShapeLayer, CardTextLayer, CardImageLayer, CardSvgLayer, CardSvgPath };

/**
 * Rounded-rect frame path in a 0..100 viewBox, with a corner radius expressed as
 * a fraction of the shorter side. Mirrors the `frame-rounded-rect` catalog shape
 * so templates and the Elementi panel produce visually identical photo slots.
 */
function roundedRectFramePath(radiusFraction = 0.11): { paths: { d: string }[]; viewBox: { width: number; height: number } } {
  const r = Math.max(0, Math.min(50, radiusFraction * 100));
  const d = [
    `M ${5 + r},5`,
    `Q 5,5 5,${5 + r}`,
    `V ${95 - r}`,
    `Q 5,95 ${5 + r},95`,
    `H ${95 - r}`,
    `Q 95,95 95,${95 - r}`,
    `V ${5 + r}`,
    `Q 95,5 ${95 - r},5`,
    'Z',
  ].join(' ');
  return { paths: [{ d }], viewBox: { width: 100, height: 100 } };
}

// ─────────────────────────────────────────────────────────────────────────────
// Canvas presets (mm) — must match the seeded product slot sizes.
// ─────────────────────────────────────────────────────────────────────────────

/** Greeting card face: A5 portrait, 148×210mm, NO bleed (no cutting), 5mm safe margin. */
export const GC_CANVAS = { widthMm: 148, heightMm: 210, bleedMm: 0, safeMm: 5 } as const;
/** Alias: the shared A5 face used by all greeting-card formats (flat / duplex / folded). */
export const A5_FACE = GC_CANVAS;
/** Invitation: A6 portrait, 105×148mm, 3mm bleed (single `face`). */
export const INV_CANVAS = { widthMm: 105, heightMm: 148, bleedMm: 3 } as const;
/** Menu: A5 portrait, 148×210mm, 3mm bleed (single `face`). */
export const MENU_CANVAS = { widthMm: 148, heightMm: 210, bleedMm: 3 } as const;

// ─────────────────────────────────────────────────────────────────────────────
// Layer helpers — keep transforms terse and within-bounds at the call site.
// All coordinates are mm from the trimmed top-left (bleed handled by render).
// ─────────────────────────────────────────────────────────────────────────────

export function text(
  content: LocalizedContent,
  font: string,
  size: number,
  color: string,
  t: { x: number; y: number; width: number; height: number; rotation?: number },
  z: number,
  align: 'left' | 'center' | 'right' = 'center',
): CardTextLayer {
  return {
    type: 'text',
    // Cast: template-layer content may be a LocalizedContent map; it is resolved
    // to a plain string by resolveTemplateSlots() before the canvas sees it.
    content: content as string,
    font,
    size,
    color,
    align,
    z,
    transform: { x: t.x, y: t.y, width: t.width, height: t.height, rotation: t.rotation ?? 0 },
  };
}

/**
 * A decorative vector motif placed in the design (corner floral, sparkle cluster,
 * confetti, geometric divider, botanical sprig …). `paths` carry their own per-path
 * fill/stroke (so multi-colour motifs keep their colours); pass `color` to recolour
 * the whole motif a single hue. `rotation` lets the same motif be mirrored into all
 * four corners. Transform is mm; the motif's viewBox is scaled to fill the box.
 */
export function svg(
  paths: CardSvgPath[],
  viewBox: { width: number; height: number },
  t: { x: number; y: number; width: number; height: number; rotation?: number },
  z: number,
  color?: string,
  gradient?: CardGradient,
): CardSvgLayer {
  return {
    type: 'svg',
    paths,
    viewBox,
    color,
    gradient,
    z,
    transform: { x: t.x, y: t.y, width: t.width, height: t.height, rotation: t.rotation ?? 0 },
  };
}

export function rect(
  t: { x: number; y: number; width: number; height: number; rotation?: number },
  z: number,
  opts: { fill?: string; fillGradient?: CardGradient; stroke?: string; strokeWidth?: number; radiusMm?: number },
): CardShapeLayer {
  return {
    type: 'shape',
    shape: 'rect',
    fill: opts.fill,
    fillGradient: opts.fillGradient,
    stroke: opts.stroke,
    strokeWidth: opts.strokeWidth,
    radiusMm: opts.radiusMm,
    z,
    transform: { x: t.x, y: t.y, width: t.width, height: t.height, rotation: t.rotation ?? 0 },
  };
}

export function line(
  t: { x: number; y: number; width: number; rotation?: number },
  z: number,
  opts: { stroke: string; strokeWidth: number },
): CardShapeLayer {
  return {
    type: 'shape',
    shape: 'line',
    stroke: opts.stroke,
    strokeWidth: opts.strokeWidth,
    z,
    transform: { x: t.x, y: t.y, width: t.width, height: 0, rotation: t.rotation ?? 0 },
  };
}

/**
 * A real photo slot the customer drops a photo INTO — a framed `CardImageLayer`
 * with `sourceKey: ''` (empty) and a rounded-rect clip shape. While empty it
 * renders as a placeholder (outline + "+ Dodaj fotografijo" hint) in both the
 * editor canvas and the print render; once filled, the photo is cover-fit and
 * clipped to the rounded frame.
 *
 * Returns `CardLayer[]` (a single-element array) so existing template call sites
 * (`...photoFrame(...)`) keep working unchanged.
 *
 * `opts.radiusFraction` controls corner roundness (fraction of the 0..100
 * viewBox; default 0.11 ≈ the catalog rounded-rect). The legacy rect/text/hint
 * options are accepted but ignored — the empty-state visuals are now owned by
 * the renderer.
 */
export function photoFrame(
  t: { x: number; y: number; width: number; height: number; rotation?: number },
  z: number,
  opts: {
    radiusFraction?: number;
    // Legacy options (ignored; kept for call-site compatibility):
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    radiusMm?: number;
    hint?: string;
    hintColor?: string;
    hintFont?: string;
    hintSize?: number;
  } = {},
): CardLayer[] {
  const layer: CardImageLayer = {
    type: 'image',
    sourceKey: '',
    frame: roundedRectFramePath(opts.radiusFraction),
    z,
    transform: {
      x: t.x,
      y: t.y,
      width: t.width,
      height: t.height,
      rotation: t.rotation ?? 0,
    },
  };
  return [layer];
}
